import { useAppDispatch, useAppSelector } from "../../app/hooks";
import { SideMenuPageContainer } from "../../components/side_menu_page_container/SideMenuPageContainer";
import Grid2 from "@mui/material/Unstable_Grid2/Grid2";
import {
	inputDataChanged,
	formatChanged,
	exportFormatChanged,
	widthChanged,
	heightChanged,
	fontSizeChanged,
	textColorChanged,
	backgroundColorChanged,
	lineColorChanged,
	displayValueChanged,
	textAlignChanged,
	textPositionChanged,
	marginChanged,
	generateBarcodeAsync,
	BarcodeFormat,
	BarcodeExportFormat,
	selectState,
} from "./BarcodeGeneratorSlice";
import {
	Box,
	Divider,
	InputAdornment,
	MenuItem,
	Stack,
	TextField,
	Typography,
	FormControlLabel,
	Switch,
} from "@mui/material";
import { CustomSelect } from "../../components/custom_select/CustomSelect";
import { CustomButton } from "../../components/custom_button/CustomButton";
import FileSaveIcon from "@mui/icons-material/SaveOutlined";
import { HomeCard } from "../../components/home_card/HomeCard";
import QrCodeGeneratorImage from "../../assets/QrCodeGenerator.webp";
import PasswordGeneratorImage from "../../assets/PasswordGenerator.webp";
import { useTranslation } from "react-i18next";
import AdSense from "../../components/adsense/AdSense";
import { ADSENSE_SLOTS } from "../../config/adsense";

export function BarcodeGenerator() {
	const dispatch = useAppDispatch();
	const state = useAppSelector(selectState);
	const { t } = useTranslation();

	return (
		<SideMenuPageContainer
			pageTitle={t("barcodeGenerator.title")}
			pageSeoTitle={t("barcodeGenerator.seoTitle")}
			pageDescription={t("barcodeGenerator.description")}
		>
			<Box sx={{ width: { xs: 300, sm: 350, md: 600, lg: 700 } }}>
				<Grid2
					container
					spacing={2}
					sx={{ mt: 2 }}
					columns={{ xs: 6, sm: 6, md: 12, lg: 12 }}
				>
					<Grid2 xs={12}>
						<Typography variant="h2" sx={{ mb: 2 }}>
							{t("barcodeGenerator.mainHeading")}
						</Typography>
					</Grid2>
					
					<Grid2 xs={12}>
						<TextField
							fullWidth
							label={t("barcodeGenerator.dataLabel")}
							variant="outlined"
							color="info"
							value={state.inputData}
							onChange={(e) => {
								dispatch(inputDataChanged(e.target.value));
							}}
							helperText={t("barcodeGenerator.dataHelper")}
						/>
					</Grid2>

					<Grid2 xs={6}>
						<CustomSelect
							label={t("barcodeGenerator.barcodeFormat")}
							value={state.format}
							onChange={(e) => {
								dispatch(formatChanged(e.target.value as BarcodeFormat));
							}}
						>
							<MenuItem value={BarcodeFormat.CODE128}>CODE128</MenuItem>
							<MenuItem value={BarcodeFormat.CODE39}>CODE39</MenuItem>
							<MenuItem value={BarcodeFormat.EAN13}>EAN13</MenuItem>
							<MenuItem value={BarcodeFormat.EAN8}>EAN8</MenuItem>
							<MenuItem value={BarcodeFormat.UPC}>UPC</MenuItem>
							<MenuItem value={BarcodeFormat.ITF}>ITF</MenuItem>
							<MenuItem value={BarcodeFormat.MSI}>MSI</MenuItem>
							<MenuItem value={BarcodeFormat.pharmacode}>Pharmacode</MenuItem>
							<MenuItem value={BarcodeFormat.codabar}>Codabar</MenuItem>
						</CustomSelect>
					</Grid2>

					<Grid2 xs={6}>
						<CustomSelect
							label={t("barcodeGenerator.exportAs")}
							value={state.exportFormat}
							onChange={(e) => {
								dispatch(exportFormatChanged(Number(e.target.value) as BarcodeExportFormat));
							}}
						>
							<MenuItem value={BarcodeExportFormat.png}>PNG</MenuItem>
							<MenuItem value={BarcodeExportFormat.jpg}>JPG</MenuItem>
							<MenuItem value={BarcodeExportFormat.webp}>WEBP</MenuItem>
							<MenuItem value={BarcodeExportFormat.svg}>SVG</MenuItem>
						</CustomSelect>
					</Grid2>

					<Grid2 xs={6}>
						<TextField
							fullWidth={true}
							label={t("barcodeGenerator.barWidth")}
							variant="outlined"
							color="info"
							type="number"
							value={state.width}
							onChange={(e) => {
								dispatch(widthChanged(Number(e.target.value)));
							}}
							InputProps={{
								endAdornment: (
									<InputAdornment position="end">
										<Typography>{t("barcodeGenerator.px")}</Typography>
									</InputAdornment>
								),
							}}
							inputProps={{ min: 1, max: 10 }}
						/>
					</Grid2>

					<Grid2 xs={6}>
						<TextField
							fullWidth={true}
							label={t("barcodeGenerator.height")}
							variant="outlined"
							color="info"
							type="number"
							value={state.height}
							onChange={(e) => {
								dispatch(heightChanged(Number(e.target.value)));
							}}
							InputProps={{
								endAdornment: (
									<InputAdornment position="end">
										<Typography>{t("barcodeGenerator.px")}</Typography>
									</InputAdornment>
								),
							}}
							inputProps={{ min: 20, max: 500, color: "info" }}
						/>
					</Grid2>

					<Grid2 xs={6}>
						<TextField
							fullWidth={true}
							label={t("barcodeGenerator.fontSize")}
							variant="outlined"
							color="info"
							type="number"
							value={state.fontSize}
							onChange={(e) => {
								dispatch(fontSizeChanged(Number(e.target.value)));
							}}
							InputProps={{
								endAdornment: (
									<InputAdornment position="end">
										<Typography>{t("barcodeGenerator.px")}</Typography>
									</InputAdornment>
								),
							}}
							inputProps={{ min: 8, max: 48 }}
						/>
					</Grid2>

					<Grid2 xs={6}>
						<TextField
							fullWidth={true}
							label={t("barcodeGenerator.margin")}
							variant="outlined"
							color="info"
							type="number"
							value={state.margin}
							onChange={(e) => {
								dispatch(marginChanged(Number(e.target.value)));
							}}
							InputProps={{
								endAdornment: (
									<InputAdornment position="end">
										<Typography>{t("barcodeGenerator.px")}</Typography>
									</InputAdornment>
								),
							}}
							inputProps={{ min: 0, max: 50 }}
						/>
					</Grid2>

					<Grid2 xs={6}>
						<TextField
							fullWidth={true}
							type="color"
							label={t("barcodeGenerator.lineColor")}
							value={state.lineColor}
							InputLabelProps={{ shrink: true }}
							onChange={(e) => {
								dispatch(lineColorChanged(e.target.value));
							}}
						/>
					</Grid2>

					<Grid2 xs={6}>
						<TextField
							fullWidth={true}
							type="color"
							label={t("barcodeGenerator.backgroundColor")}
							value={state.backgroundColor}
							InputLabelProps={{ shrink: true }}
							onChange={(e) => {
								dispatch(backgroundColorChanged(e.target.value));
							}}
						/>
					</Grid2>

					<Grid2 xs={12}>
						<FormControlLabel
							control={
								<Switch
									checked={state.showText}
									onChange={(e) => {
										dispatch(displayValueChanged(e.target.checked));
									}}
									color="primary"
								/>
							}
							label={t("barcodeGenerator.showText")}
						/>
					</Grid2>

					<Grid2 xs={6} sx={{display: state.showText ? "block" : "none"}}>
						<CustomSelect
							label={t("barcodeGenerator.textAlignment")}
							value={state.textAlign}
							onChange={(e) => {
								dispatch(textAlignChanged(e.target.value as "left" | "center" | "right"));
							}}
						>
							<MenuItem value="left">{t("barcodeGenerator.left")}</MenuItem>
							<MenuItem value="center">{t("barcodeGenerator.center")}</MenuItem>
							<MenuItem value="right">{t("barcodeGenerator.right")}</MenuItem>
						</CustomSelect>
					</Grid2>

					<Grid2 xs={6} sx={{display: state.showText ? "block" : "none"}}>
						<CustomSelect
							label={t("barcodeGenerator.textPosition")}
							value={state.textPosition}
							onChange={(e) => {
								dispatch(textPositionChanged(e.target.value as "top" | "bottom"));
							}}
						>
							<MenuItem value="top">{t("barcodeGenerator.top")}</MenuItem>
							<MenuItem value="bottom">{t("barcodeGenerator.bottom")}</MenuItem>
						</CustomSelect>
					</Grid2>

					<Grid2 xs={12}>
						<Stack
							direction="column"
							justifyContent="center"
							alignItems="center"
						>
							<Box sx={{ mt: 1 }}>
								<CustomButton
									icon={<FileSaveIcon sx={{ color: "#FFD9A8" }} />}
									disabled={
										state.inputData === undefined || state.inputData === ""
									}
									label={t("barcodeGenerator.download")}
									width={200}
									onClick={() => {
										dispatch(generateBarcodeAsync());
									}}
								/>
							</Box>
						</Stack>
					</Grid2>

					<Grid2 xs={12} sx={{ mt: 3, mb: 3 }}>
						<Divider sx={{ backgroundColor: "#7895a1" }}></Divider>
					</Grid2>

					<Grid2 xs={12}>
						<AdSense
							adSlot={ADSENSE_SLOTS.IN_ARTICLE}
							adFormat="fluid"
							adLayoutKey="-fb+5w+4e-db+86"
							sx={{ mb: 3 }}
							/>

						<Typography variant="h3" sx={{ mb: 2 }}>
							{t("barcodeGenerator.content.freeHeading")}
						</Typography>
						<Typography variant="body1" sx={{ mb: 3 }}>
							{t("barcodeGenerator.content.freeDescription")}
						</Typography>

						<Typography variant="h3" sx={{ mb: 2 }}>
							{t("barcodeGenerator.content.formatsHeading")}
						</Typography>
						<Typography variant="body1" sx={{ mb: 3 }}>
							{t("barcodeGenerator.content.formatsDescription")}
						</Typography>

						<Typography variant="h3" sx={{ mb: 2 }}>
							{t("barcodeGenerator.content.customizeHeading")}
						</Typography>
						<Typography variant="body1" sx={{ mb: 3 }}>
							{t("barcodeGenerator.content.customizeDescription")}
						</Typography>

						<Typography variant="h3" sx={{ mb: 2 }}>
							{t("barcodeGenerator.content.secureHeading")}
						</Typography>
						<Typography variant="body1" sx={{ mb: 3 }}>
							{t("barcodeGenerator.content.secureDescription")}
						</Typography>

						<Typography variant="h3" sx={{ mb: 2 }}>
							{t("barcodeGenerator.content.businessHeading")}
						</Typography>
						<Typography variant="body1" sx={{ mb: 3 }}>
							{t("barcodeGenerator.content.businessDescription")}
						</Typography>
					</Grid2>

					<Grid2 xs={12}>
						<Typography variant="h3" sx={{ mt: 3, mb: 1 }}>
							{t("barcodeGenerator.related")}
						</Typography>
					</Grid2>

					<Grid2 xs={6}>
						<HomeCard
							languageResource="qrCodeGenerator"
							link="/QrCodeGenerator"
							smallCard={true}
							image={QrCodeGeneratorImage}
						/>
					</Grid2>
					<Grid2 xs={6}>
						<HomeCard
							languageResource="passwordGenerator"
							link="/PasswordGenerator"
							smallCard={true}
							image={PasswordGeneratorImage}
						/>
					</Grid2>
				</Grid2>
			</Box>
		</SideMenuPageContainer>
	);
}
